import React, { useState } from "react";
import { ChatCircleDots, PaperPlaneRight, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AutoGrowTextarea } from "@/components/AutoGrowTextarea";

const RATING_LABELS = { 1: "Poor", 2: "Fair", 3: "Good", 4: "Great", 5: "Excellent" };

export const FeedbackButton = ({ context, variant = "header", open: controlledOpen, onOpenChange }) => {
  const [openState, setOpenState] = useState(false);
  const open = onOpenChange ? !!controlledOpen : openState;
  const setOpen = onOpenChange || setOpenState;
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [busy, setBusy] = useState(false);

  const canSend = message.trim().length >= 3 || rating > 0;

  const send = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setBusy(true);
    try {
      await api.post("/feedback", { message: message.trim(), rating: rating || null, page: context || window.location.pathname });
      toast.success(rating ? "Thanks for the rating and feedback!" : "Thanks — your feedback was sent!");
      setMessage("");
      setRating(0);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {variant === "menu" ? null : variant === "lesson" ? (
        <button
          data-testid="lesson-feedback-button"
          onClick={() => setOpen(true)}
          className="btn-ghost text-xs font-semibold flex items-center gap-1.5 text-[#000000] border border-[#C4C8CE] rounded-full px-3 py-1.5 bg-white hover:border-[#CCFF00] cursor-pointer"
        >
          <ChatCircleDots size={15} weight="duotone" color="#52525B" /> Send feedback to developer
        </button>
      ) : (
        <button
          data-testid="feedback-button"
          onClick={() => setOpen(true)}
          title="Send feedback to developer"
          className="h-9 flex items-center gap-1.5 px-3 rounded-full bg-[#FFFFFF] border border-[#C4C8CE] hover:border-[#CCFF00] transition-colors cursor-pointer text-xs font-semibold text-[#000000] whitespace-nowrap"
        >
          <ChatCircleDots size={16} weight="duotone" color="#52525B" />
          <span className="hidden sm:inline">Send feedback to developer</span>
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Send feedback</DialogTitle>
            <DialogDescription>Spotted a glitch or have an idea? It goes straight to the team.</DialogDescription>
          </DialogHeader>
          <form onSubmit={send} className="flex flex-col gap-3">
            <div className="rounded-xl border border-[#C4C8CE] bg-[#FAFAFA] px-4 py-3" data-testid="feedback-rating-block">
              <div className="text-xs font-bold uppercase tracking-[0.12em] text-[#000000] mb-1.5">Rate us</div>
              <div className="flex items-center gap-1" onMouseLeave={() => setHoverRating(0)}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = n <= (hoverRating || rating);
                  return (
                    <button
                      key={n}
                      type="button"
                      data-testid={`feedback-star-${n}`}
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      className="btn-ghost p-0.5 cursor-pointer transition-transform hover:scale-110"
                      onMouseEnter={() => setHoverRating(n)}
                      onClick={() => setRating(n === rating ? 0 : n)}
                    >
                      <Star size={28} weight={active ? "fill" : "regular"} color={active ? "#FBBC04" : "#A1A1AA"} />
                    </button>
                  );
                })}
                <span className="ml-2 text-sm font-semibold text-[#000000] min-w-[70px]" data-testid="feedback-rating-label">
                  {(hoverRating || rating) ? RATING_LABELS[hoverRating || rating] : ""}
                </span>
              </div>
            </div>
            <AutoGrowTextarea
              data-testid="feedback-message"
              className="input-base"
              minRows={2}
              maxHeight={220}
              placeholder={rating ? "Anything you'd like to add? (optional)" : "What's on your mind?"}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
            />
            <button data-testid="feedback-submit" type="submit" className="btn-primary flex items-center justify-center gap-2" disabled={busy || !canSend}>
              <PaperPlaneRight size={15} weight="bold" /> {busy ? "Sending…" : "Send feedback"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackButton;
